import { useState } from "react";
import { Link } from "react-router";
import { format, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Card, Skeleton } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { ApptDayCard } from "./ApptDayCard";
import {
  activeApptsOnDay,
  agendaViewDays,
  agendaViewRange,
  type AgendaViewMode,
  type WeekAppointment,
} from "@/lib/dashboard-week";
import { APPOINTMENT_STATUS_COLORS } from "@bookdental/schemas";
import { MonthCalendarGrid } from "./MonthCalendarGrid";
import { WeekDayStrip } from "./WeekDayStrip";
import { AppointmentDetailDialog } from "./AppointmentDetailDialog";

const MAX_PER_DAY = 4;
const MAX_DAY_GROUPS = 8;

const VIEW_OPTIONS: Array<{ key: AgendaViewMode; label: string }> = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];

const VIEW_TITLE: Record<AgendaViewMode, string> = {
  day: "Agenda do dia",
  week: "Agenda da semana",
  month: "Agenda do mês",
};

function rangeLabel(mode: AgendaViewMode): string {
  const { start, end } = agendaViewRange(mode);
  if (mode === "day") {
    const label = format(start, "EEEE, d 'de' MMMM", { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (mode === "month") {
    const label = format(start, "MMMM 'de' yyyy", { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  return `${format(start, "d", { locale: ptBR })}–${format(end, "d 'de' MMM", { locale: ptBR })}`;
}

type Props = {
  appts: WeekAppointment[];
  loading: boolean;
  viewMode: AgendaViewMode;
  onViewModeChange: (mode: AgendaViewMode) => void;
  /** Quando presente, desvia o clique em compromisso de OUTRA organização pra fora do
   *  AppointmentDetailDialog (que é só-leitura da própria org) — ver `FiltroDeOrganizacoes`. */
  onSelectForeign?: (appt: WeekAppointment) => void;
  /** Quando true, esconde os links "Ver agenda completa"/"+N consulta(s)" — eles apontam pra
   *  /agenda da organização ATIVA, o que fica errado quando este widget mostra o preview de uma
   *  organização diferente (ver organization/painel-geral/AgirNaOrganizacao.tsx). */
  readOnly?: boolean;
};

export function WeekAgenda({
  appts,
  loading,
  viewMode,
  onViewModeChange,
  onSelectForeign,
  readOnly = false,
}: Props) {
  const days = agendaViewDays(viewMode);
  const [selectedAppt, setSelectedAppt] = useState<WeekAppointment | null>(null);
  // Ligado por padrão — mesma decisão do InicioAgendaCard: "Mostrar tudo" é o estado inicial de
  // toda agenda que tem o toggle.
  const [showAllMonth, setShowAllMonth] = useState(true);

  const handleSelectAppt = (appt: WeekAppointment) => {
    if (appt.ehDeOutraOrganizacao && onSelectForeign) {
      onSelectForeign(appt);
      return;
    }
    setSelectedAppt(appt);
  };

  return (
    <Card className="p-5 sm:p-6">
      <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary">
            <CalendarDays className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">
              {VIEW_TITLE[viewMode]}
            </h2>
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              {rangeLabel(viewMode)}
            </p>
          </div>
        </div>

        <div className="flex justify-start sm:justify-center">
          <div className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
            {VIEW_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => onViewModeChange(key)}
                className={cn(
                  "inline-flex h-8 w-20 items-center justify-center rounded text-sm font-medium transition-colors",
                  viewMode === key
                    ? "bg-brand-600 font-semibold text-white"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-start gap-3 sm:justify-end">
          {!readOnly && (
            <Link
              to="/agenda"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 dark:text-primary"
            >
              Ver agenda completa
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      {viewMode === "month" && !loading && (
        <label className="mt-4 flex w-fit items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-muted-foreground">
          <input
            type="checkbox"
            checked={showAllMonth}
            onChange={(e) => setShowAllMonth(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
          />
          Mostrar tudo
        </label>
      )}

      {/* Semana e mês mostram tudo dentro dos próprios dias; só "dia" usa a lista abaixo. */}
      <div className="mt-5 space-y-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : viewMode === "week" ? (
          <WeekDayStrip
            days={days}
            appts={appts}
            onSelectAppt={handleSelectAppt}
            readOnly={readOnly}
          />
        ) : viewMode === "month" ? (
          <MonthCalendarGrid
            appts={appts}
            showAll={showAllMonth}
            onSelectAppt={handleSelectAppt}
            readOnly={readOnly}
          />
        ) : (
          <AgendaList
            days={days}
            appts={appts}
            viewMode={viewMode}
            onSelectAppt={handleSelectAppt}
            readOnly={readOnly}
          />
        )}
      </div>

      <AppointmentDetailDialog appointment={selectedAppt} onClose={() => setSelectedAppt(null)} />
    </Card>
  );
}

/** Exportado pra ser reaproveitado por InicioAgendaCard (view "Dia" da agenda de consultas). */
export function AgendaList({
  days,
  appts,
  viewMode,
  onSelectAppt,
  readOnly = false,
}: {
  days: Date[];
  appts: WeekAppointment[];
  viewMode: Exclude<AgendaViewMode, "month">;
  onSelectAppt: (appt: WeekAppointment) => void;
  readOnly?: boolean;
}) {
  const daysWithActivity = days
    .map((d) => ({
      day: d,
      appts: activeApptsOnDay(appts, d),
    }))
    .filter((g) => g.appts.length > 0);

  if (daysWithActivity.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-zinc-500 sm:py-6 dark:text-muted-foreground">
        Nenhuma consulta agendada para {viewMode === "day" ? "hoje" : "esta semana"}.
      </p>
    );
  }

  const visibleGroups = daysWithActivity.slice(0, MAX_DAY_GROUPS);
  const hiddenGroups = daysWithActivity.length - visibleGroups.length;

  return (
    <>
      {visibleGroups.map(({ day, appts: dayAppts }) => (
        <div key={day.toISOString()}>
          <p
            className={cn(
              "mb-1.5 text-xs font-semibold uppercase tracking-wide",
              isSameDay(day, new Date())
                ? "text-brand-700 dark:text-primary"
                : "text-zinc-400 dark:text-muted-foreground",
            )}
          >
            {format(day, "EEEE, d 'de' MMM", { locale: ptBR })}
          </p>
          {/* Dia usa cartão (largura de sobra, e ali se decide qual consulta é); Semana segue em
              linha, que é o que cabe com sete grupos empilhados. */}
          {/* A visão Dia mostra TUDO: ela responde "o que tem hoje", e um "+2 consulta(s)" no fim
              transformava a resposta em meia resposta — logo no lugar onde só existe um dia pra
              caber. O teto continua valendo na Semana, onde são sete grupos empilhados. */}
          <div className={viewMode === "day" ? "space-y-2" : "space-y-1"}>
            {(viewMode === "day" ? dayAppts : dayAppts.slice(0, MAX_PER_DAY)).map((a) =>
              viewMode === "day" ? (
                <ApptDayCard key={a.id} appt={a} onSelect={onSelectAppt} />
              ) : (
                <ApptRow key={a.id} appt={a} onSelect={onSelectAppt} />
              ),
            )}
            {viewMode !== "day" && dayAppts.length > MAX_PER_DAY && (
              readOnly ? (
                <p className="block px-2 py-1 text-xs font-medium text-zinc-400 dark:text-muted-foreground">
                  +{dayAppts.length - MAX_PER_DAY} consulta(s)
                </p>
              ) : (
                <Link
                  to="/agenda"
                  className="block px-2 py-1 text-xs font-medium text-brand-700 hover:underline dark:text-primary"
                >
                  +{dayAppts.length - MAX_PER_DAY} consulta(s)
                </Link>
              )
            )}
          </div>
        </div>
      ))}
      {hiddenGroups > 0 && (
        readOnly ? (
          <p className="block text-center text-xs font-medium text-zinc-400 dark:text-muted-foreground">
            +{hiddenGroups} dia(s) com consultas
          </p>
        ) : (
          <Link
            to="/agenda"
            className="block text-center text-xs font-medium text-brand-700 hover:underline dark:text-primary"
          >
            +{hiddenGroups} dia(s) com consultas — ver agenda completa
          </Link>
        )
      )}
    </>
  );
}

function ApptRow({ appt, onSelect }: { appt: WeekAppointment; onSelect: (appt: WeekAppointment) => void }) {
  const color = appt.color ?? APPOINTMENT_STATUS_COLORS[appt.status];
  const name = appt.patient?.name ?? appt.patientName;
  const isForeign = !!appt.ehDeOutraOrganizacao;

  return (
    <button
      type="button"
      onClick={() => onSelect(appt)}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/50",
        isForeign && "ring-1 ring-dashed ring-zinc-200 dark:ring-border",
      )}
    >
      <span className="w-11 shrink-0 text-xs font-semibold tabular-nums text-zinc-500 dark:text-muted-foreground">
        {appt.startTime}
      </span>
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate text-sm text-zinc-800 dark:text-foreground">
        {name}
      </span>
      {isForeign && (
        <span className="hidden max-w-[30%] shrink truncate text-xs text-zinc-400 dark:text-muted-foreground sm:inline">
          {appt.organizationName}
        </span>
      )}
      {!isForeign && appt.procedure && (
        <span className="hidden max-w-[40%] shrink truncate text-xs text-zinc-400 dark:text-muted-foreground sm:inline">
          {appt.procedure}
        </span>
      )}
    </button>
  );
}
